import { defaultRound } from './round.js';
/**
 * Generic yearly <-> monthly rate conversion, shared by every domain whose host code routes
 * through `commonRentCalc` (lease base/effective/asking rent, property avg/min/max asking rates,
 * availability min/max asking rate):
 *
 *   yearly  = round(monthly * 12)
 *   monthly = round(yearly  / 12)
 *
 * Rounding is delegated to `deps.round` (Angular adapter supplies the real locale-aware pipeline;
 * Node/tests use {@link defaultRound}). A null input yields a null output (clear-on-empty).
 */
/** Months in a year — the basis of every yearly<->monthly rate conversion. */
export const MONTHS_PER_YEAR = 12;
/** Scalar monthly -> yearly (× 12). Also the canonical home for the `multiplyBy12` helpers
 *  duplicated across components (asking-rate, clipboard). */
export function multiplyBy12(value) {
  return value * MONTHS_PER_YEAR;
}
/** Scalar yearly -> monthly (÷ 12). */
export function divideBy12(value) {
  return value / MONTHS_PER_YEAR;
}
export function yearlyFromMonthlyRule(pair) {
  return {
    id: `${pair.idPrefix}.yearly-from-monthly`,
    inputs: [pair.monthly],
    outputs: [pair.yearly],
    formulaVersion: '1.0.0',
    run: (ctx, deps) => {
      const round = deps?.round ?? defaultRound;
      const monthly = ctx.get(pair.monthly);
      ctx.set(pair.yearly, monthly == null ? null : round(multiplyBy12(Number(monthly)), pair.yearly));
    }
  };
}
export function monthlyFromYearlyRule(pair) {
  return {
    id: `${pair.idPrefix}.monthly-from-yearly`,
    inputs: [pair.yearly],
    outputs: [pair.monthly],
    formulaVersion: '1.0.0',
    run: (ctx, deps) => {
      const round = deps?.round ?? defaultRound;
      const yearly = ctx.get(pair.yearly);
      ctx.set(pair.monthly, yearly == null ? null : round(divideBy12(Number(yearly)), pair.monthly));
    }
  };
}
/** Build both directions for one rate pair. */
export function rentConversionRules(pair) {
  return [yearlyFromMonthlyRule(pair), monthlyFromYearlyRule(pair)];
}
